import React, { forwardRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { mediaQueries } from 'shared/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Button, CheckoutAccordion } from 'shared/components';
import { ModalWrapper } from 'shared/modals/v2-wrapper';
import useStores from 'shared/hooks/use-stores';
import useApolloClient from 'shared/hooks/use-apollo-client';
import { RewardsRibbon, RibbonColorPallettes } from 'shared/assets/rewards-ribbon';
import AlpineIQTerms from 'shared/modals/alpineiq-terms';
import { CheckoutText } from 'checkout/components/styles/common';
import { createAlpineIQUser, createSpringbigMember, createDPOSLoyaltyMember } from 'src/state/actions/cart';
import getRewardsV2 from 'shared/graphql/reward/queries/get-rewards-V2.gql';
import { formatBirthdayForRewards } from 'shared/helpers/rewards';

import { StyledCheckoutAccordionSummary, SuccessText } from 'checkout/components/sections/rewards/styles';
import { setLoyaltyData } from 'src/components/loyalty/loyalty.helpers';

function RewardsSignUp({ springbigRewardsEnabled, alpineRewardsEnabled, dutchiePosLoyaltyEnabled }) {
  const { Cart, User, UI } = useStores();
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(false);

  const flags = useFlags();

  async function createSpringbigAccount() {
    const successfullyCreated = await createSpringbigMember({
      userId: User.id,
      dispensaryId: Cart.order.dispensary.id,
      apolloClient,
    });

    if (!successfullyCreated) {
      return false;
    }

    const { data } = await apolloClient.query({
      query: getRewardsV2,
      variables: {
        dispensaryId: Cart.order.dispensary.id,
        phoneNumber: User.phone,
        dob: formatBirthdayForRewards(User.birthday),
        customerId: User.id,
        pin: '',
      },
    });

    if (!data?.getRewardsV2) {
      return false;
    }

    setLoyaltyData({
      user: User,
      cart: Cart,
      data,
      dispensaryId: Cart.order.dispensary.id,
    });

    return successfullyCreated;
  }

  async function createAlpineIQAccount() {
    const alpineiqV2Flag = flags['migration.Rewards-Accounts-Create-Endpoint'];
    return createAlpineIQUser({ Cart, User, apolloClient, alpineiqV2Flag });
  }

  async function createDutchiePOSLoyaltyAccount() {
    return createDPOSLoyaltyMember({
      userId: User.id,
      dispensaryId: Cart.order.dispensary.id,
      apolloClient,
    });
  }

  async function handleSignup() {
    try {
      if (springbigRewardsEnabled) {
        return await createSpringbigAccount();
      }
      if (alpineRewardsEnabled) {
        return await createAlpineIQAccount();
      }
      if (dutchiePosLoyaltyEnabled) {
        return await createDutchiePOSLoyaltyAccount();
      }
      return false;
    } catch (error) {
      console.error('Error creating rewards account', error);
      return false;
    }
  }

  function openTermsModal() {
    UI.showAlpineIQTermsModal = true;
  }

  function closeTermsModal() {
    UI.showAlpineIQTermsModal = false;
  }

  async function onPressAgree() {
    closeTermsModal();
    setLoading(true);

    const successfullyCreated = await handleSignup();

    setSuccessful(successfullyCreated);
    setLoading(false);

    if (!successfullyCreated) {
      UI.showErnie('We were unable to complete your enrollment. Please try again later. ', `danger`);
    }
  }

  return (
    <div>
      {successful && (
        <CheckoutAccordion>
          <StyledCheckoutAccordionSummary>
            <Flex alignItems='center'>
              <RewardsRibbon pallette={RibbonColorPallettes.green} height={35.36} width={35.36} />
              <SuccessText>{Cart.rewardsProgramDisplayName} enrollment successful!</SuccessText>
            </Flex>
          </StyledCheckoutAccordionSummary>
        </CheckoutAccordion>
      )}

      {!successful && (
        <StyledCheckoutAccordion expanded>
          <>
            <RewardsRibbon pallette={RibbonColorPallettes.blue} height={66.64} width={66.64} />
            <TitleText>Enroll in {`${Cart.rewardsProgramDisplayName}!`}</TitleText>
            <BodyText>{Cart.rewardsProgramDisplayEnrollment}</BodyText>
            <StyledButton onClick={openTermsModal} loading={loading}>
              Sign me up!
            </StyledButton>
          </>

          {!UI.isEmbedded && UI.showAlpineIQTermsModal && (
            <AlpineIQTerms onAgree={onPressAgree} onDecline={closeTermsModal} />
          )}
          {UI.isEmbedded && (
            <ModalWrapper open={!!UI.showAlpineIQTermsModal}>
              <AlpineIQTerms onAgree={onPressAgree} onDecline={closeTermsModal} />
            </ModalWrapper>
          )}
        </StyledCheckoutAccordion>
      )}
    </div>
  );
}

export default observer(forwardRef(RewardsSignUp));

const StyledButton = styled(Button)`
  align-self: flex-start;
  margin-top: 26px;
  margin: 0 auto;
  padding: 0 13px;
  line-height: 20px;
  font-size: 12px;
  width: 97px;
  height: 27px;
`;

const TitleText = styled(Text)`
  padding-top: 14px;
  padding-bottom: 5px;
  margin: 0 auto;
  line-height: 175%;
  font-size: 14px;
  font-weight: bold;

  color: var(--dcm-checkout-text-color);
`;

const BodyText = styled(CheckoutText)`
  padding-bottom: 20px;
  text-align: center;
  line-height: 16px;
  font-size: 12px;
  display: inline-flex;

  color: var(--dcm-checkout-text-color);

  @media ${mediaQueries.largePhone} {
    width: 230px;
    padding-bottom: 16px;
  }
`;

const StyledCheckoutAccordion = styled(CheckoutAccordion)`
  margin: 0 auto;
  text-align: center;
  padding: 25px 70px 39px;
  height: 252px;

  @media ${mediaQueries.largePhone} {
    padding: 25px 10px !important;
    height: 240px;
  }
`;
